import { pageColumnClasses } from "@/components/layout/contentLayout.tsx";
import { cn, getApiOrigin, resolveApiPath } from "@/lib/utils.ts";
import { connectErrorAdminLink, connectErrorCopy } from "@/shared/connectErrorCopy.ts";
import { parseOnboardingThreadId } from "@/shared/onboardingLanding.ts";
import { slackTeamIdSchema } from "@/types/slackChannels.ts";
import { SLACK_INSTALL_TEAM_PARAM, slackInstallPath } from "@/types/slackSignIn.ts";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "wouter";

// Shared result page for all OAuth flows (login, account linking, add-account, etc.).
//
// Paths that lead here:
//  1. autoClose=true + flow="login" — login success: broadcast and close immediately.
//  2. autoClose=true + flow="connect" — connector add from Settings: broadcast and close.
//  3. autoClose=true + flow="add_account" — add-account from homepage switcher: broadcast
//     (incl. accountId, already_added, same_account, email for Case A) and close.
//  4. No autoClose (Zoom from chat artifact) — countdown then close.
export const OAuthResult = () => {
  const [params] = useSearchParams();
  const provider = params.get("provider") ?? "the service";
  // Human-readable connector name (e.g. an OpenAPI spec's name like "Google Docs")
  // for providers whose `provider` value is a generic type like "openapi".
  const connectorDisplayName = params.get("displayName");
  const success = params.get("success") === "true";
  const autoClose = params.get("autoClose") === "true";
  const flow = params.get("flow") ?? "connect";
  const returnTo = params.get("returnTo");
  const error = params.get("error");
  // Built from a fixed path, never from the URL, so this page can't be made to
  // show someone else's link.
  const adminLink = connectErrorAdminLink(error, getApiOrigin());
  const accountId = params.get("accountId");
  const alreadyAdded = params.get("already_added") === "true";
  const sameAccount = params.get("same_account") === "true";
  const email = params.get("email");
  // Validated here rather than downstream: this page's params are whatever a
  // link put in them, and the listener turns this one into a route.
  const onboardingThreadId = parseOnboardingThreadId(params.get("onboardingThreadId"));
  // Validated for the same reason: the listener turns it into a request.
  const teamId = parseSlackTeamId(params.get("teamId"));
  // A Slack sign-up goes on to install okthx into the workspace it signed in
  // with. Only a workspace id is taken from the link; the path is fixed.
  const slackInstall =
    success && flow === "login" && provider === "slack"
      ? slackInstallPath(params.get(SLACK_INSTALL_TEAM_PARAM))
      : undefined;
  const [countdown, setCountdown] = useState(5);

  // React Strict Mode double-fires effects in development. Guard against
  // duplicate BroadcastChannel messages.
  const didRunEffect = useRef(false);

  useEffect(() => {
    if (didRunEffect.current) return;
    didRunEffect.current = true;

    // Notify the opener tab (Settings/Auth/Home listens via BroadcastChannel)
    const channel = new BroadcastChannel("oauth_result");
    channel.postMessage({
      provider,
      displayName: connectorDisplayName,
      success,
      flow,
      returnTo,
      error,
      accountId: accountId ? Number(accountId) : undefined,
      alreadyAdded,
      sameAccount,
      email,
      onboardingThreadId,
      autoClose,
      teamId,
    });
    channel.close();

    // After the broadcast, so the opener is signed in whatever the install
    // does — a workspace that needs admin approval never redirects back.
    if (slackInstall) {
      window.location.replace(resolveApiPath(slackInstall));
    } else if (autoClose) {
      window.close();
    }
  }, [
    success,
    provider,
    connectorDisplayName,
    autoClose,
    flow,
    returnTo,
    error,
    accountId,
    alreadyAdded,
    sameAccount,
    email,
    onboardingThreadId,
    teamId,
    slackInstall,
  ]);

  // Auto-close the tab after countdown (non-autoClose flows like Zoom). A
  // failure that hands the user an admin link stays open so they can copy it.
  useEffect(() => {
    if (autoClose || adminLink) return;
    if (countdown <= 0) {
      window.close();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [adminLink, autoClose, countdown]);

  const isLogin = flow === "login";
  const displayName = connectorDisplayName ?? provider.charAt(0).toUpperCase() + provider.slice(1);

  // autoClose flows (login / connect / add-account from the main app) close themselves
  // immediately after broadcasting. Rendering the success text creates a jarring
  // "Google connected successfully!" flash in the closing tab; render nothing instead so
  // the tab disappears cleanly. The parent tab is responsible for user feedback.
  if (autoClose) {
    return <div className="h-dvh" />;
  }

  // Fallback UI for non-autoClose flows (e.g., Zoom connect from a chat artifact).
  return (
    <div className={cn("flex flex-col grow-1 justify-center h-dvh", pageColumnClasses["8"])}>
      <div className="flex flex-col grow-1 items-center justify-center gap-4">
        {success ? (
          <>
            <h2 className="text-2xl font-semibold">
              {isLogin ? "Login successful!" : `${displayName} connected successfully!`}
            </h2>
            <p className="text-center text-muted-foreground">
              You can close this tab. It will close automatically in {countdown} seconds.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-semibold">
              {isLogin ? "Sign-in failed" : "Connection failed"}
            </h2>
            <p className="text-center text-muted-foreground">
              {isLogin
                ? "Something went wrong signing in. Please try again."
                : (connectErrorCopy(error) ??
                  `Something went wrong connecting ${displayName}. Please try again.`)}
            </p>
            {!isLogin && adminLink ? (
              <a href={adminLink} className="text-center text-sm underline break-all">
                {adminLink}
              </a>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

/** A Slack workspace id from the query string, or `undefined` if it isn't one. */
function parseSlackTeamId(value: string | null): string | undefined {
  const parsed = slackTeamIdSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
